import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Phone, Star } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, fileUrl } from "@/lib/api";
import { IMAGES } from "@/constants/images";
import { Reveal, MaskLine } from "@/components/Reveal";
import Marquee from "@/components/site/Marquee";
import ReviewForm from "@/components/site/ReviewForm";
import { useSeo } from "@/lib/seo";

export default function Home() {
  const { t, lang, siteInfo, services: svcData } = useApp();
  useSeo("home");
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);
  const heroScale = useTransform(scrollY, [0, 600], [1.05, 1.15]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data.slice(0, 3))).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data.slice(0, 3))).catch(() => {});
  }, []);

  const tx = (obj, fallback = "") => (obj && (obj[lang] || obj.de)) || fallback;
  const sv = (k, f, fb) => tx(svcData?.[k]?.[f]) || fb;

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden bg-brand pt-24">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand via-brand/60 to-brand/20" />
        </motion.div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-16 md:pb-24">
          <motion.img src="/logo-mark.png" alt="AM-Montage Logo" data-testid="hero-logo"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="h-24 md:h-32 w-auto mb-6 drop-shadow-xl" />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/70 mb-6">
            {t.hero.kicker}
          </motion.div>
          <h1 className="font-display font-black text-white text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.92]">
            <MaskLine delay={0.15}>{t.hero.l1}</MaskLine>
            <MaskLine delay={0.3}>{t.hero.l2} <span className="text-accent2">{t.hero.l3}</span></MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }}
            className="mt-10 flex flex-wrap gap-4">
            <Link to="/kontakt" data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-accent2 text-white font-semibold px-7 py-4 hover:bg-white hover:text-brand transition-colors">
              {t.hero.cta1} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/projekte" data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold px-7 py-4 hover:bg-white/10 transition-colors">
              {t.hero.cta2}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-brand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10 border-x border-white/10">
          {t.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="py-10 px-6 text-center">
              <div className="font-display font-black text-4xl lg:text-5xl text-white">{s.n}</div>
              <div className="mt-1 text-xs uppercase tracking-widest text-slate-400">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <Reveal>
          <div className="flex items-end justify-between gap-6 mb-14">
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-accent2">01 — {t.nav.services}</span>
              <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl tracking-tight max-w-xl">{t.services.title}</h2>
            </div>
            <p className="hidden md:block max-w-xs text-slate-600">{t.services.sub}</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { key: "fenster", img: IMAGES.window, name: sv("fenster", "name", t.services.fenster.name), desc: sv("fenster", "desc", t.services.fenster.desc) },
            { key: "sonnenschutz", img: IMAGES.sun, name: sv("sonnenschutz", "name", t.services.sonnenschutz.name), desc: sv("sonnenschutz", "desc", t.services.sonnenschutz.desc) },
          ].map((s, i) => (
            <Reveal key={s.key} delay={i * 0.1}>
              <Link to="/leistungen" data-testid={`service-card-${s.key}`}
                className="group block border border-slate-200 hover:border-brand transition-colors">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={s.img} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-2xl">{s.name}</h3>
                    <ArrowUpRight className="text-slate-300 group-hover:text-accent2 transition-colors" />
                  </div>
                  <p className="mt-3 text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      {projects.length > 0 && (
        <section className="bg-slate-50 border-y border-slate-200 py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="flex items-end justify-between gap-6 mb-14">
                <div>
                  <span className="text-sm font-semibold uppercase tracking-widest text-accent2">02 — {t.nav.gallery}</span>
                  <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl tracking-tight">{t.gallery.title}</h2>
                </div>
                <Link to="/projekte" className="hidden md:inline-flex items-center gap-2 font-semibold hover:text-accent2 transition-colors">
                  {t.gallery.all} <ArrowRight size={18} />
                </Link>
              </div>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {projects.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <Link to={`/projekte/${p.id}`} className="group block bg-white border border-slate-200 hover:border-brand transition-colors">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={fileUrl(p.cover_image || p.after_image) || IMAGES.facade} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-5">
                      <div className="text-xs uppercase tracking-widest text-accent2">{p.location}</div>
                      <h3 className="mt-1.5 font-display font-bold text-xl">{tx(p.title)}</h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS + REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32" data-testid="reviews-section">
        <Reveal>
          <span className="text-sm font-semibold uppercase tracking-widest text-accent2">03 — {t.nav.testimonials}</span>
          <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl tracking-tight mb-8">{t.testimonials.title}</h2>
        </Reveal>
        {siteInfo.google_url && (
          <Reveal className="mb-14">
            <div data-testid="google-reviews-badge"
              className="inline-flex flex-wrap items-center gap-x-6 gap-y-3 border border-slate-200 bg-slate-50 px-6 py-4">
              <span className="flex items-center gap-3">
                <svg width="26" height="26" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/>
                </svg>
                <span className="font-display font-bold">{t.contact.googleTitle}</span>
              </span>
              {siteInfo.google_rating && (
                <span className="flex items-center gap-2" data-testid="google-rating">
                  <span className="font-display font-black text-2xl">{siteInfo.google_rating}</span>
                  <span className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={16} className={n <= Math.round(parseFloat(String(siteInfo.google_rating).replace(",", "."))) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />)}
                  </span>
                </span>
              )}
              <a href={siteInfo.google_url} target="_blank" rel="noreferrer" data-testid="google-review-btn"
                className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-5 py-2.5 hover:bg-accent2 transition-colors">
                {t.contact.googleBtn} <ArrowUpRight size={16} />
              </a>
            </div>
          </Reveal>
        )}
        {testimonials.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {testimonials.map((tm, i) => (
              <Reveal key={tm.id} delay={i * 0.08} className="border border-slate-200 p-7 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1">
                    {Array.from({ length: tm.rating || 5 }).map((_, k) => <Star key={k} size={16} className="fill-accent2 text-accent2" />)}
                  </div>
                  {tm.source === "google" && (
                    <span data-testid={`google-source-${tm.id}`} title="Google-Rezension" className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                      <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
                        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                        <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/>
                      </svg>
                      Google
                    </span>
                  )}
                </div>
                <p className="text-slate-700 leading-relaxed flex-1">"{tx(tm.text)}"</p>
                <div className="mt-6 flex items-center gap-3 pt-5 border-t border-slate-100">
                  <span className="w-10 h-10 bg-brand text-white grid place-items-center font-display font-bold">{tm.name?.[0]}</span>
                  <div>
                    <div className="font-semibold">{tm.name}</div>
                    <div className="text-sm text-slate-500">{tm.location}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
        <Reveal delay={0.1} className="max-w-2xl">
          <ReviewForm />
        </Reveal>
      </section>

      {/* TEAM */}
      <section className="bg-slate-50 border-y border-slate-200 py-20 md:py-32" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-widest text-accent2">04 — {t.team.kicker}</span>
            <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl tracking-tight">{t.team.title}</h2>
            <p className="mt-4 max-w-xl text-slate-600 mb-14">{t.team.sub}</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {t.team.members.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.1} className="bg-white border border-slate-200 p-8 flex flex-col sm:flex-row gap-6">
                {m.photo ? (
                  <img src={m.photo} alt={m.name} data-testid={`team-photo-${i}`}
                    className="w-28 h-28 shrink-0 object-cover border border-slate-200" />
                ) : (
                  <div data-testid={`team-avatar-${i}`}
                    className="w-24 h-24 shrink-0 bg-brand text-white grid place-items-center font-display font-black text-3xl">
                    {m.name.split(" ").map((p) => p[0]).join("")}
                  </div>
                )}
                <div>
                  <h3 className="font-display font-bold text-2xl">{m.name}</h3>
                  <div className="mt-1 text-xs font-semibold uppercase tracking-widest text-accent2">{m.role || t.team.role}</div>
                  <p className="mt-3 text-slate-600 leading-relaxed">{m.desc}</p>
                  <a href={`tel:${m.phone.replace(/\s/g, "")}`} data-testid={`team-phone-${i}`}
                    className="mt-4 inline-flex items-center gap-2 font-semibold text-brand hover:text-accent2 transition-colors">
                    <Phone size={16} /> {m.phone}
                  </a>
                  {m.phone2 && (
                    <a href={`tel:${m.phone2.replace(/\s/g, "")}`} data-testid={`team-phone2-${i}`}
                      className="mt-1 ml-6 flex items-center gap-2 font-semibold text-brand hover:text-accent2 transition-colors">
                      <Phone size={16} /> {m.phone2}
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]">{t.contact.title}</h2>
            <p className="mt-5 text-white/70 max-w-md">{t.contact.sub}</p>
          </Reveal>
          <Reveal delay={0.1} className="md:justify-self-end">
            <ul className="space-y-3 mb-8">
              {t.about.chapters.map((c, i) => (
                <li key={i} className="flex items-center gap-3 text-white/80"><Check size={18} className="text-accent2" />{c.t}</li>
              ))}
            </ul>
            <Link to="/kontakt" className="inline-flex items-center gap-2 bg-accent2 text-white font-semibold px-7 py-4 hover:bg-white hover:text-brand transition-colors">
              {t.nav.quote} <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
